import React, { useState, useEffect } from "react";
import AgentContextCard from "./AgentContextCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { ChevronsUpDownIcon, ExternalLinkIcon, Loader2Icon, ChevronDownIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { AgentInternalRequestType } from "../../types/AgentInternalRequestType";
import { AgentService } from "../../services/AgentService";
import { usePermissions } from "../../PermissionsProvider";

export interface SessionContext {
  agent_id: number;
  context_data: {
    agent_name: string;
    model?: string;
    start_time?: number;
    end_time?: number;
    context_string?: string;
    context?: string;
    s3_image_key?: string;
    image_presigned_url?: string;
    [key: string]: any;
  };
  operation_type: string;
  request_id: string;
  request_operation: string;
  timestamp: number;
}

interface AgentContextProps {
  contexts: SessionContext[];
  selectedRequestId?: string;
  showHeader?: boolean;
  isLoading?: boolean;
}

const AgentContext: React.FC<AgentContextProps> = ({
  contexts,
  selectedRequestId,
  showHeader = false,
  isLoading = false
}) => {
  const { apiKey } = usePermissions();
  
  // Find the selected context based on request ID
  const selectedContext = contexts.find(ctx => ctx.request_id === selectedRequestId);
  
  // State for fresh presigned URL
  const [imagePresignedUrl, setImagePresignedUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  
  // Fetch fresh presigned URL when s3_image_key changes
  useEffect(() => {
    const s3ImageKey = selectedContext?.context_data?.s3_image_key;
    
    // Reset state when context changes
    setImagePresignedUrl(null);
    
    if (!s3ImageKey) {
      return;
    }
    
    // If we have a cached presigned URL that's not expired, use it
    // Otherwise fetch a fresh one
    const cachedUrl = selectedContext?.context_data?.image_presigned_url;
    if (cachedUrl) {
      // Use cached URL initially, it may still be valid
      setImagePresignedUrl(cachedUrl);
    }
    
    // Fetch fresh presigned URL
    setImageLoading(true);
    const subscription = AgentService.getFilePresignedUrl(s3ImageKey, () => {}, apiKey).subscribe(
      (response: any) => {
        if (response?.presigned_url) {
          setImagePresignedUrl(response.presigned_url);
        }
        setImageLoading(false);
      },
      (error: any) => {
        console.error("Failed to fetch presigned URL:", error);
        setImageLoading(false);
        // Fall back to cached URL if available
        if (cachedUrl) {
          setImagePresignedUrl(cachedUrl);
        }
      }
    );
    
    return () => subscription.unsubscribe();
  }, [selectedContext?.context_data?.s3_image_key, apiKey]);
  
  // Track which sections are open by their tag identifier
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  // Track if image section is open (default open)
  const [imageOpen, setImageOpen] = useState(true);
  
  // Toggle section open/closed
  const toggleSection = (tag: string) => {
    setOpenSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      return newSet;
    });
  };

  
  // Define hidden tags that should not be displayed
  const hiddenTags = ["agent_name","request", "instructions"];
  
  // Parse context strings
  const parseContextString = (contextStr: string) => {
    const tagRegex = /<([^>]+)>([\s\S]*?)<\/\1>/g;
    const sections: Array<{ tag: string; content: string }> = [];
    let match;
    
    while ((match = tagRegex.exec(contextStr)) !== null) {
      sections.push({
        tag: match[1],
        content: match[2].trim()
      });
    }
    
    return sections;
  };

  const getOperationTypeClasses = (type?: string) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border";
    switch (type?.toLowerCase()) {
      case AgentInternalRequestType.RESPOND.toLowerCase():
      case AgentInternalRequestType.FINAL_RESPONSE.toLowerCase():
        return `${baseClasses} border-green-500 text-green-700 dark:text-white bg-card [background-image:linear-gradient(rgba(34,197,94,0.1),rgba(34,197,94,0.1))] dark:[background-image:linear-gradient(rgba(34,197,94,0.2),rgba(34,197,94,0.2))]`;
      case AgentInternalRequestType.ROUTE.toLowerCase():
        return `${baseClasses} border-blue-500 text-blue-700 dark:text-white bg-card [background-image:linear-gradient(rgba(59,130,246,0.1),rgba(59,130,246,0.1))] dark:[background-image:linear-gradient(rgba(59,130,246,0.2),rgba(59,130,246,0.2))]`;
      case AgentInternalRequestType.ORCHESTRATE.toLowerCase():
      case AgentInternalRequestType.ORCHESTRATION.toLowerCase():
        return `${baseClasses} border-purple-500 text-purple-700 dark:text-white bg-card [background-image:linear-gradient(rgba(168,85,247,0.1),rgba(168,85,247,0.1))] dark:[background-image:linear-gradient(rgba(168,85,247,0.2),rgba(168,85,247,0.2))]`;
      case AgentInternalRequestType.EXTRACT.toLowerCase():
        return `${baseClasses} border-orange-500 text-orange-700 dark:text-white bg-card [background-image:linear-gradient(rgba(251,146,60,0.1),rgba(251,146,60,0.1))] dark:[background-image:linear-gradient(rgba(251,146,60,0.2),rgba(251,146,60,0.2))]`;
      default:
        return `${baseClasses} border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-card`;
    }
  };

  const getSectionTitle = (tag: string) => {
    switch (tag) {
      case "thinking":
        return "Agent Thinking";
      case "plan":
        return "Plan";
      case "result":
        return "Result";
      case "workspace":
        return "Workspace";
      case "action_log":
        return "Action Log";
      case "state":
        return "State";
      default:
        return tag.charAt(0).toUpperCase() + tag.slice(1);
    }
  };

  // Format timestamp to a nicer format
  const formatTimestamp = (timestamp: string) => {
    // Keep milliseconds but format them nicely (e.g., "02:52:43.570 PM" -> "2:52:43.57 PM")
    let formattedTime = timestamp;
    
    // Match the time pattern
    const match = formattedTime.match(/^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?\s*(AM|PM)?$/i);
    if (match) {
      const [, hours, minutes, seconds, milliseconds, period] = match;
      // Remove leading zero from hours if present
      const formattedHours = parseInt(hours, 10).toString();
      
      // Format milliseconds to 2 decimal places if present
      let formattedMs = "";
      if (milliseconds) {
        formattedMs = "." + milliseconds.substring(0, 2);
      }
      
      return `${formattedHours}:${minutes}:${seconds}${formattedMs} ${period || ""}`.trim();
    }
    
    return timestamp;
  };

  // Parse action log entries
  const parseActionLog = (content: string) => {
    const lines = content.split("\n").filter(line => line.trim());
    const entries: Array<{
      timestamp?: string;
      action: string;
      status?: string;
      details?: string;
    }> = [];

    lines.forEach((line) => {
      const trimmedLine = line.trim();
      
      // Skip empty lines or lines that are just numbers (like "02")
      if (!trimmedLine || /^\d+$/.test(trimmedLine)) {
        return;
      }
      
      // Try multiple parsing patterns
      // Pattern 1: HH:MM:SS PM [Agent Name] 'action_name' or "action_name" or `action_name`: STATUS
      let match = trimmedLine.match(/^(\d{1,2}:\d{2}:\d{2}(?:\.\d+)?\s*(?:AM|PM)?)\s*\[([^\]]+)\]\s*['"` ]([^'"`]+)['"` ]\s*:\s*(.+)$/);
      
      if (match) {
        const [, timestamp, , action, statusOrDetails] = match;
        const knownStatuses = ["RUNNING", "SUCCESS", "COMPLETED", "FAILED", "ERROR", "PENDING"];
        const isStatus = knownStatuses.includes(statusOrDetails.toUpperCase());
        
        entries.push({
          timestamp: formatTimestamp(timestamp.trim()),
          action: action.trim(),
          status: isStatus ? statusOrDetails.trim() : undefined,
          details: !isStatus ? statusOrDetails.trim() : undefined
        });
        return;
      }
      
      // Pattern 2: HH:MM:SS PM [Agent Name] action_name: STATUS (without quotes)
      match = trimmedLine.match(/^(\d{1,2}:\d{2}:\d{2}(?:\.\d+)?\s*(?:AM|PM)?)\s*\[([^\]]+)\]\s*([^:]+):\s*(.+)$/);
      
      if (match) {
        const [, timestamp, , action, statusOrDetails] = match;
        const knownStatuses = ["RUNNING", "SUCCESS", "COMPLETED", "FAILED", "ERROR", "PENDING"];
        const isStatus = knownStatuses.includes(statusOrDetails.toUpperCase());
        
        entries.push({
          timestamp: formatTimestamp(timestamp.trim()),
          action: action.trim(),
          status: isStatus ? statusOrDetails.trim() : undefined,
          details: !isStatus ? statusOrDetails.trim() : undefined
        });
        return;
      }
      
      // Fallback for other formats
      entries.push({
        action: trimmedLine,
        status: undefined,
        details: undefined
      });
    });

    return entries;
  };

  const parseState = (content: string): Array<{ key: string; value: any; isNull: boolean }> => {
    const entries: Array<{ key: string; value: any; isNull: boolean }> = [];
    if (!content) return entries;

    try {
      // Try to parse as JSON object
      const parsed = JSON.parse(content);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        Object.entries(parsed).forEach(([key, value]) => {
          entries.push({
            key,
            value,
            isNull: value === null || value === undefined || value === ""
          });
        });
      }
    } catch (e) {
      // If not valid JSON, try to parse as key-value pairs
      const lines = content.split("\n").filter(line => line.trim());
      lines.forEach(line => {
        const colonIndex = line.indexOf(":");
        if (colonIndex > -1) {
          const key = line.substring(0, colonIndex).trim();
          const valueStr = line.substring(colonIndex + 1).trim();
          
          // Check if value represents null
          const isNull = valueStr.toLowerCase() === "null" || 
                        valueStr.toLowerCase() === "none" || 
                        valueStr === "";
          
          entries.push({
            key,
            value: isNull ? null : valueStr,
            isNull
          });
        }
      });
    }

    // Sort entries: non-null values first (alphabetically), then null values (alphabetically)
    entries.sort((a, b) => {
      if (a.isNull !== b.isNull) {
        return a.isNull ? 1 : -1; // Non-null values come first
      }
      return a.key.localeCompare(b.key); // Alphabetical within each group
    });

    return entries;
  };

  const parseWorkspace = (content: string): Array<{ timestamp?: string; context: string }> => {
    const entries: Array<{ timestamp?: string; context: string }> = [];
    if (!content) return entries;

    // Try to parse as JSON array first
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        parsed.forEach((item) => {
          if (typeof item === "object" && item !== null) {
            // Look for timestamp field
            const timestamp = item.timestamp || item.time || item.created_at || item.updated_at;
            // Format JSON and remove outer braces
            const formatted = JSON.stringify(item, null, 2);
            const lines = formatted.split("\n");
            let contextContent = formatted;
            if (lines.length > 2 && lines[0] === "{" && lines[lines.length - 1] === "}") {
              // Remove first and last lines (the braces) and dedent
              contextContent = lines.slice(1, -1).map(line => line.replace(/^  /, "")).join("\n");
            }
            entries.push({
              timestamp: timestamp ? formatTimestamp(String(timestamp)) : undefined,
              context: contextContent
            });
          } else {
            entries.push({
              context: JSON.stringify(item, null, 2)
            });
          }
        });
        return entries;
      }
    } catch (e) {
      // Not valid JSON array, try other parsing methods
    }

    // Try to parse as entries with timestamps followed by JSON
    const lines = content.split("\n");
    let currentEntry: { timestamp?: string; context: string } | null = null;
    let jsonBuffer = "";
    let braceCount = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Check if this line starts with a timestamp
      const timestampMatch = trimmedLine.match(/^(\d{1,2}:\d{2}:\d{2}(?:\.\d+)?\s*(?:AM|PM)?)\s*(.*)$/) ||
                            trimmedLine.match(/^(\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)\s*(.*)$/);
      
      if (timestampMatch && braceCount === 0) {
        // Save previous entry if exists
        if (currentEntry) {
          if (jsonBuffer) {
            try {
              const parsed = JSON.parse(jsonBuffer);
              // Format JSON and remove outer braces
              const formatted = JSON.stringify(parsed, null, 2);
              const lines = formatted.split("\n");
              if (lines.length > 2 && lines[0] === "{" && lines[lines.length - 1] === "}") {
                // Remove first and last lines (the braces) and dedent
                currentEntry.context = lines.slice(1, -1).map(line => line.replace(/^  /, "")).join("\n");
              } else {
                currentEntry.context = formatted;
              }
            } catch {
              currentEntry.context = jsonBuffer.trim();
            }
          }
          entries.push(currentEntry);
          jsonBuffer = "";
        }
        
        // Start new entry
        const [, timestamp, rest] = timestampMatch;
        currentEntry = {
          timestamp: formatTimestamp(timestamp),
          context: ""
        };
        
        // Add the rest of the line to json buffer
        if (rest) {
          jsonBuffer = rest;
          braceCount += (rest.match(/{/g) || []).length;
          braceCount -= (rest.match(/}/g) || []).length;
        }
      } else if (currentEntry) {
        // Continue building the JSON buffer
        if (jsonBuffer) jsonBuffer += "\n";
        jsonBuffer += line;
        braceCount += (line.match(/{/g) || []).length;
        braceCount -= (line.match(/}/g) || []).length;
      } else {
        // No timestamp found yet, treat as single entry
        entries.push({
          context: trimmedLine
        });
      }
    }
    
    // Save last entry
    if (currentEntry) {
      if (jsonBuffer) {
        try {
          const parsed = JSON.parse(jsonBuffer);
          // Format JSON and remove outer braces
          const formatted = JSON.stringify(parsed, null, 2);
          const lines = formatted.split("\n");
          if (lines.length > 2 && lines[0] === "{" && lines[lines.length - 1] === "}") {
            // Remove first and last lines (the braces) and dedent
            currentEntry.context = lines.slice(1, -1).map(line => line.replace(/^  /, "")).join("\n");
          } else {
            currentEntry.context = formatted;
          }
        } catch {
          currentEntry.context = jsonBuffer.trim();
        }
      }
      entries.push(currentEntry);
    }

    return entries;
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toUpperCase()) {
      case "SUCCESS":
      case "COMPLETED":
        return "text-green-600 dark:text-green-400";
      case "FAILED":
      case "ERROR":
        return "text-red-600 dark:text-red-400";
      case "RUNNING":
        return "text-blue-600 dark:text-blue-400";
      case "PENDING":
        return "text-yellow-600 dark:text-yellow-400";
      default:
        return "text-muted-foreground";
    }
  };

  // Show loading skeleton if loading
  if (isLoading && selectedRequestId) {
    return (
      <div className="space-y-4">
        {showHeader && (
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-gc-med text-foreground">Agent Context</h3>
          </div>
        )}
        <div className="space-y-3">
          {/* Skeleton cards */}
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                <div className="h-4 w-4 bg-muted rounded animate-pulse" />
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (contexts.length === 0 && !isLoading) {
    return (
      <div className="text-center py-8 text-gray-500">
        No contexts found for this session.
      </div>
    );
  }

  if (!selectedContext) {
    return (
      <div className="text-center py-8">
        <div className="space-y-2">
          <p className="text-muted-foreground">Select a request from the timeline to view its context</p>
          <p className="text-sm text-muted-foreground/60">Click on any card in the timeline above</p>
        </div>
      </div>
    );
  }

  const contextStr = selectedContext.context_data?.context_string || selectedContext.context_data?.context || "";
  const parsedSections = parseContextString(contextStr);
  // Filter out agent_name sections, hidden tags, and empty/None sections
  const filteredSections = parsedSections.filter(section => {
    
    // Skip hidden tags (request, instructions)
    if (hiddenTags.includes(section.tag.toLowerCase())) {
      return false;
    }
    
    // Skip empty or None sections
    const content = section.content.trim();
    if (content === "" || content.toLowerCase() === "none") {
      return false;
    }
    
    return true;
  });
  
  // If no context_string, show available data
  if (!contextStr && selectedContext) {
    // console.log("No context_string found, showing available context_data:", selectedContext.context_data);
  }
  
  // Check if we have an actual empty context (not still loading)
  const hasEmptyContext = selectedContext && contextStr === "" && !isLoading;

  const handleExpandCollapseAll = () => {
    // If any sections are expanded (not in openSections due to inverted logic), collapse all
    // Otherwise, expand all
    const allSectionTags = filteredSections.map(s => s.tag);
    const anyExpanded = allSectionTags.some(tag => !openSections.has(tag));
    
    if (anyExpanded) {
      // Collapse all (add all to openSections)
      setOpenSections(new Set(allSectionTags));
    } else {
      // Expand all (clear openSections)
      setOpenSections(new Set());
    }
  };

  // Check if any sections are expanded
  const anyExpanded = filteredSections.some(section => !openSections.has(section.tag));

  return (
    <div className="space-y-4">
      {/* Header with expand/collapse button */}
      {showHeader && filteredSections.length > 0 && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-gc-med text-foreground">Agent Context</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExpandCollapseAll}
            className="gap-2"
          >
            <ChevronsUpDownIcon className="w-4 h-4" />
            {anyExpanded ? "Collapse All" : "Expand All"}
          </Button>
        </div>
      )}
      
      {/* Context Image - display if available (styled like AgentContextCard) */}
      {selectedContext?.context_data?.s3_image_key && (
        <Card className="overflow-hidden">
          <CardContent 
            className="p-3 hover:bg-accent/50 transition-colors cursor-pointer"
            onClick={() => setImageOpen(!imageOpen)}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Context Image</h3>
              <div className="flex items-center gap-2">
                {imagePresignedUrl && (
                  <a
                    href={imagePresignedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Open <ExternalLinkIcon className="w-3 h-3" />
                  </a>
                )}
                <ChevronDownIcon 
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    !imageOpen && "-rotate-90"
                  )} 
                />
              </div>
            </div>
          </CardContent>
          {imageOpen && (
            <CardContent className="px-3 pb-3 pt-0">
              <div className="border-t border-border pt-3">
                {imageLoading && !imagePresignedUrl ? (
                  <div className="flex items-center justify-center h-32 bg-muted/20 rounded">
                    <Loader2Icon className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : imagePresignedUrl ? (
                  <img
                    src={imagePresignedUrl}
                    alt="Agent image context"
                    className="w-full h-auto rounded max-h-[300px] object-contain bg-muted/20"
                    onError={(e) => {
                      // Hide the image if it fails to load
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-32 bg-muted/20 rounded text-muted-foreground text-sm">
                    Failed to load image
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Context Sections */}
      {filteredSections.length > 0 ? (
        <div className="space-y-3">
          {filteredSections.map((section) => (
            <AgentContextCard
              key={section.tag}
              title={getSectionTitle(section.tag)}
              content={section.content}
              tag={section.tag}
              isOpen={!openSections.has(section.tag)} // Default open (inverted logic)
              onToggle={() => toggleSection(section.tag)}
              parseActionLog={section.tag === "action_log" ? parseActionLog : undefined}
              parseWorkspace={section.tag === "workspace" ? parseWorkspace : undefined}
              parseState={section.tag === "state" ? parseState : undefined}
            />
          ))}
        </div>
      ) : hasEmptyContext ? (
        // Only show "no context" when we definitively have an empty context
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground text-center py-8">
            No context data available for this request.
          </div>
        </div>
      ) : (
        // Show available metadata when context_string is missing but we have other data
        selectedContext && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              No detailed context available. Showing metadata:
            </div>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
              <div><span className="font-medium">Agent:</span> {selectedContext.context_data?.agent_name}</div>
              <div><span className="font-medium">Model:</span> {selectedContext.context_data?.model}</div>
              <div><span className="font-medium">Operation:</span> {selectedContext.operation_type}</div>
              <div><span className="font-medium">Request ID:</span> {selectedContext.request_id}</div>
              {selectedContext.context_data?.task_id && (
                <div><span className="font-medium">Task ID:</span> {selectedContext.context_data.task_id}</div>
              )}
              {selectedContext.context_data?.start_time && selectedContext.context_data?.end_time && (
                <div><span className="font-medium">Duration:</span> {selectedContext.context_data.end_time - selectedContext.context_data.start_time}ms</div>
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default AgentContext;
